// ---- Skills (client mirror of server-owned progression) ----
// XP is DOING: the server owns every per-skill XP bar (charCtx / playerSkills) and
// grants it in defeatNpc by the killing blow's style, streaming a `skills` message
// (net.ts) whenever it changes — with the initial set riding a follow-up to the join
// `spawn` (like quests). This module is a read-only mirror: it holds the latest
// authoritative rows and surfaces an "+N Bar XP" toast on a gain. The client can
// never mint XP — there is no writer here, only setSkills from the wire. The Skills
// book HUD (a later checkpoint) reads these same rows.

import type { NetSkill, NetStanding } from "./shared.ts";

let skills: NetSkill[] = [];
let skillsHydrated = false;     // the first setSkills is the join baseline, not a gain
let influence = 0;              // global fungible skill-point pool (server-owned)
let standings: NetStanding[] = [];
let title: string | null = null; // equipped floating title (highest earned rank)

/** Capitalize a bar name for display ("melee" → "Melee"). Shared by the toast + the HUD. */
export function barLabel(bar: string): string {
  return bar.charAt(0).toUpperCase() + bar.slice(1);
}

/** Mirror the authoritative skill rows from the server (`skills` message / join).
 *  Diffs against the previous set to toast any XP gain — a pure client concern, so
 *  the server needn't send deltas. The FIRST call is the join hydration of
 *  already-earned XP (sent right after `spawn`); it seeds the baseline SILENTLY,
 *  else previously-earned XP would flash as a phantom "+N Melee XP" on load. A
 *  reconnect re-hydrates against the last-known set, so its diff is zero anyway. */
export function setSkills(rows: NetSkill[]): void {
  const prev = skills;
  if (skillsHydrated) {
    for (const row of rows) {
      const before = prev.find((r) => r.skill === row.skill);
      for (const [bar, xp] of Object.entries(row.xp)) {
        const gain = xp - (before?.xp[bar] ?? 0);
        if (gain > 0) {
          // Lazy: inventory imports hasBoxUnlocked from here — avoid a static cycle.
          void import("./inventory.ts").then((m) => m.flashToast(`+${gain} ${barLabel(bar)} XP`));
        }
      }
    }
  }
  skills = rows;
  skillsHydrated = true;
}

/** Current XP in one bar of one skill (0 if unknown) — a read for the HUD. */
export function skillXp(skill: string, bar: string): number {
  return skills.find((r) => r.skill === skill)?.xp[bar] ?? 0;
}

/** Unlocked box ids for a skill (empty if unknown) — a read for the HUD. */
export function skillBoxes(skill: string): string[] {
  return skills.find((r) => r.skill === skill)?.boxes ?? [];
}

/** Is this box unlocked in ANY skill? The client mirror of the server's hasBox
 *  (weapon-type + move gates) — pure UX; the server re-validates every use. */
export function hasBoxUnlocked(boxId: string): boolean {
  return skills.some((r) => r.boxes.includes(boxId));
}

// --- Influence (global pool) + Standing + Title (Plan 09 Checkpoint 2) ---------
// All server-owned; mirrored from the `influence` / `standing` messages (net.ts).

export function setInfluence(n: number): void { influence = Math.max(0, Math.floor(n)); }
/** Current global skill-point pool — the HUD's spend budget (server re-validates). */
export function influenceAmount(): number { return influence; }

export function setStanding(list: NetStanding[], t: string | null): void { standings = list; title = t; }
/** Standing on one track (rank + progress), or undefined if none yet. */
export function standingOf(track: string): NetStanding | undefined { return standings.find((s) => s.track === track); }
/** The equipped floating title (highest earned rank), or null. */
export function equippedTitle(): string | null { return title; }
